import { useTranslation } from 'react-i18next';
import { RhfTextField, type RhfTextFieldProps } from './shared/RhfTextField';

export type DisplayNameFieldProps = Omit<RhfTextFieldProps, 'label'> & {
  /** Override the default label from `m-organization:form.display_name.title`. */
  label?: string;
};

/**
 * `DisplayNameField` — RHF-bound text input for `Org.name`
 * (the display name of the organisation, e.g. "Acme Logistics").
 *
 * Defaults are sourced from `@hero/lang` (`m-organization` →
 * `form.display_name`). Consumers only need to pass `name` (the
 * RHF dotted path):
 *
 * ```tsx
 * <DisplayNameField name="displayName" required />
 * ```
 *
 * Note: this is the **display** name, *not* the legal registration
 * name. The two are intentionally separate — many organisations
 * register under a long formal name (`شركة الطريق الرقمي
 * للخدمات اللوجستية`) but want to surface a shorter brand on the
 * platform.
 */
export function DisplayNameField(props: DisplayNameFieldProps) {
  const { t } = useTranslation('m-organization');
  return (
    <RhfTextField
      {...props}
      label={props.label ?? t('form.display_name.title')}
      placeholder={props.placeholder ?? t('form.display_name.placeholder')}
      helper={props.helper ?? t('form.display_name.helper')}
    />
  );
}
